import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenant } from '../../../contexts/TenantContext';
import { supabase } from '../../../lib/supabase';
import Sidebar from '../../admin-dashboard/components/Sidebar';

/**
 * Admin V2 Recruitment — Pipeline de recrutement (table recruitment_pipeline réelle).
 * Route : /admin/recruitment + /admin/v2/recruitment.
 * Feature : pages.recruitment.admin (OFF par défaut — enrichi par scraping Phase 2).
 *
 * Lit `recruitment_pipeline` (existe). Affiche les prospects groupés par status.
 */
const AdminV2Recruitment = () => {
  const { t } = useTranslation();
  const { userProfile } = useAuth();
  const { tenantName, tenantId, agencyId } = useTenant();
  const [collapsed, setCollapsed] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true); setError(null);
    try {
      let q = supabase
        .from('recruitment_pipeline')
        .select('id, tiktok_username, server, status, source, note, next_action_date, created_at')
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: false });
      if (agencyId) q = q.eq('agency_id', agencyId);
      const { data, error: e1 } = await q.limit(1000);
      if (e1) throw e1;
      setRows(data || []);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [tenantId, agencyId]);

  useEffect(() => { load(); }, [load]);

  const grouped = rows.reduce((acc, r) => {
    (acc[r.status] = acc[r.status] || []).push(r);
    return acc;
  }, {});

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      <Sidebar collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} userEmail={userProfile?.email} />
      <main className="flex-1 min-w-0 overflow-x-hidden">
        <header className="border-b border-border bg-card sticky top-0 z-10">
          <div className="px-4 md:px-6 py-4">
            <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground">{t('recruitV2.title', 'Recruitment')}</h1>
            <p className="text-xs text-muted-foreground mt-0.5">{tenantName} · {rows.length}</p>
          </div>
        </header>
        <div className="p-4 md:p-6 space-y-4">
          <div className="rounded-2xl bg-primary/5 border border-primary/20 p-4 text-xs text-muted-foreground">
            <span className="font-semibold text-primary mr-1">{t('recruitV2.phase2Label', 'Phase 2:')}</span>
            {t('recruitV2.phase2Note', 'Prospect discovery and live-check enrichment come from scraping (Phase 2). This page reads the recruitment_pipeline table live — manual entries also appear here.')}
          </div>

          {error && (
            <div className="rounded-2xl bg-error/5 border border-error/30 p-4 text-sm text-error flex items-center justify-between">
              <span>{String(error.message || error)}</span>
              <button type="button" onClick={load} className="px-3 py-1.5 rounded-lg border border-error/40 hover:bg-error/10 text-xs font-medium">{t('common.retry', 'Retry')}</button>
            </div>
          )}

          {loading ? (
            <div className="rounded-2xl bg-card border border-border h-48 skeleton" />
          ) : rows.length === 0 ? (
            <div className="rounded-2xl bg-card border border-border p-8 text-center text-sm text-muted-foreground">
              {t('recruitV2.empty', 'No prospects in the pipeline yet.')}
            </div>
          ) : (
            Object.entries(grouped).map(([status, list]) => (
              <section key={status} className="rounded-2xl bg-card border border-border overflow-hidden">
                <div className="px-5 py-2.5 border-b border-border bg-surface-2 flex items-center justify-between">
                  <span className="font-heading font-semibold text-foreground text-sm capitalize">{status}</span>
                  <span className="text-xs text-muted-foreground">{list.length}</span>
                </div>
                <ul className="divide-y divide-border">
                  {list.map((p) => (
                    <li key={p.id} className="px-5 py-2.5 flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <a href={`https://www.tiktok.com/@${p.tiktok_username}`} target="_blank" rel="noopener noreferrer"
                          className="font-medium text-foreground hover:text-primary truncate block">@{p.tiktok_username}</a>
                        <div className="text-xs text-muted-foreground truncate">
                          {p.server || '—'}{p.source ? ` · ${p.source}` : ''}{p.note ? ` · ${p.note}` : ''}
                        </div>
                      </div>
                      {p.next_action_date && (
                        <span className="text-[10px] text-muted-foreground shrink-0">
                          {new Date(p.next_action_date).toLocaleDateString()}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            ))
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminV2Recruitment;
